import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from '@/pages/organization/components/InventoryUserListDialog';
import {
  listHookGroupUsers,
  listAccountHookGroupUsers,
} from '@kirin/api-client/api/hooks';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';
import { HOOK_STALE_TIME_MS } from './constants';
import { type HookTabScope, hookScopeKey } from './scope';

interface HookUsersModalProps {
  scope: HookTabScope;
  groupKey: string | null;
  onClose: () => void;
}

/**
 * Users-with-this-hook list (KIR-3643) — opened from the Hooks table Users
 * column. Mirrors the AI-Instructions users modal (shared
 * `InventoryUserListDialog`, User / Projects / Last Seen columns) and reuses the
 * existing `/hooks/:groupKey/users` endpoint (org or personal scope).
 */
export function HookUsersModal({
  scope,
  groupKey,
  onClose,
}: HookUsersModalProps): ReactElement {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.hooks.users(hookScopeKey(scope), groupKey ?? ''),
    queryFn: () => {
      // `enabled` gates this to groupKey !== null; the guard narrows the type
      // without an assertion (and never throws in practice).
      if (groupKey === null) {
        throw new Error('hook users query ran without a groupKey');
      }
      return scope.kind === 'organization'
        ? listHookGroupUsers(apiClient, scope.organizationId, groupKey)
        : listAccountHookGroupUsers(apiClient, groupKey);
    },
    enabled: groupKey !== null,
    staleTime: HOOK_STALE_TIME_MS,
  });

  const users = data?.users ?? [];

  return (
    <InventoryUserListDialog
      open={groupKey !== null}
      onClose={onClose}
      title={`Users with this hook (${isLoading ? '...' : users.length})`}
      description={
        <>
          Projects listed per user are where this hook was seen;
          &quot;Global&quot; means no project scope was reported.
        </>
      }
      columns={[
        { label: 'User' },
        { label: 'Projects' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={isError ? true : null}
      isEmpty={users.length === 0}
      emptyMessage="No users found"
      loadingMessage="Loading users..."
      errorMessage="Failed to load users. Please try again."
      onRetry={() => void refetch()}
      isRetrying={isFetching}
    >
      {users.map(user => (
        <TableRow key={user.userId}>
          <TableCell>
            <div>
              <div className="font-medium">{user.userName || 'Unknown'}</div>
              {user.userEmail && (
                <div className="text-sm text-muted-foreground">
                  {user.userEmail}
                </div>
              )}
            </div>
          </TableCell>
          <TableCell>
            {user.projects.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {user.projects.map(project => (
                  <Badge key={project} variant="outline" className="text-xs">
                    {project}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">Global</span>
            )}
          </TableCell>
          <TableCell>
            <RelativeTimeWithTooltip date={user.lastSeenAt} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
